import React, { useCallback, useEffect, useState } from 'react';
import { getInvoiceStripeUrl, listOpenInvoices } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import CtaButton from '@/components/CtaButton';

/**
 * The single-invoice path (Card 29 escape hatch + `/invoice?t=` links):
 * find the oldest open invoice and hand the parent to Stripe's hosted
 * invoice page, where payment happens entirely on Stripe.
 */
const InvoiceRedirectScreen = ({ studentId, studentName, onBack }) => {
	const [state, setState] = useState('loading'); // loading | empty | error
	const [error, setError] = useState(null);

	const go = useCallback(async () => {
		setState('loading');
		setError(null);
		try {
			const invoices = await listOpenInvoices(studentId);
			const open = (Array.isArray(invoices) ? invoices : []).sort((a, b) =>
				String(a.due_date ?? '').localeCompare(String(b.due_date ?? ''))
			);

			if (!open.length) {
				setState('empty');
				return;
			}

			const { url } = await getInvoiceStripeUrl(open[0].id);
			if (!url) throw { stripeMessage: 'The invoice’s payment page isn’t ready yet — try again in a moment.' };
			window.location.replace(url);
		} catch (err) {
			setError(parseHostedError(err).message);
			setState('error');
		}
	}, [studentId]);

	useEffect(() => {
		go();
	}, [go]);

	const backLink = onBack ? (
		<button type="button" onClick={onBack} className="w-full text-center text-[13px] font-semibold text-[#028afd]">
			← Back to Auto-Pay
		</button>
	) : null;

	if (state === 'empty') {
		return (
			<div className="flex flex-col items-center gap-[18px] pt-[12px] text-center">
				<div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#e7f4e4] text-[32px]">
					🎉
				</div>
				<h1 className="text-[24px] font-extrabold text-[#16181d]">You’re all paid up!</h1>
				<p className="text-[14px] leading-[21px] text-[#7d7d7d]">
					{studentName ?? 'Your student'} has no open invoices right now.
				</p>
				{backLink}
			</div>
		);
	}

	if (state === 'error') {
		return (
			<div className="flex flex-col gap-[18px] pt-[12px]">
				<ErrorBanner>{error}</ErrorBanner>
				<CtaButton onClick={go}>Try again</CtaButton>
				{backLink}
			</div>
		);
	}

	return <Spinner label="Finding your invoice…" />;
};

export default InvoiceRedirectScreen;
